import {
  OktonSearchSelect,
  OktonPageHeader,
  OktonInput,
  OktonAlert,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import CaixaField from '../../../features/caixa/CaixaField';
import BankAppPage from '../../../layout/BankAppPage';
import {
  usaControlaDRE,
  useParametros,
  usePlanosDFC,
} from '../../../services/hooks/useCadastros';
import { useContasBancarias } from '../../../services/hooks/useContasBancarias';
import { useCreateSaque } from '../../../services/hooks/useSaques';
import { limparEntradaValor, valorParaNumero } from '../../../utils/valorInput';
import { EstadoNavegacaoMonitor } from '../../monitor-bancario/estadoNavegacao';

const VAZIO = {
  conta: '',
  data: new Date().toISOString().split('T')[0],
  valor: '',
  planocontas: '',
  caixa: '',
  observacao: '',
};

const NovoSaque = () => {
  const history = useHistory();
  const { state } = useLocation<EstadoNavegacaoMonitor | undefined>();
  const destino =
    state?.voltarPara === '/monitor-bancario' ? '/monitor-bancario' : '/saques';
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const { data: contas = [] } = useContasBancarias();
  const { data: planosDFC = [] } = usePlanosDFC();
  const { data: parametros } = useParametros();
  const controlaDRE = usaControlaDRE(parametros);
  const createMutation = useCreateSaque();

  const [form, setForm] = useState({ ...VAZIO, conta: state?.conta ?? '' });
  const voltar = () => {
    if (destino === '/monitor-bancario') {
      history.push(destino, { conta: form.conta, periodo: state?.periodo });
    } else {
      history.push(destino);
    }
  };
  const [error, setError] = useState('');

  const salvar = async () => {
    if (!form.conta) {
      setError('Informe a Conta Bancária.');
      return;
    }
    if (!form.data) {
      setError('Informe a data do Saque.');
      return;
    }
    const valorNumerico = valorParaNumero(form.valor);
    if (!valorNumerico) {
      setError('Informe um valor maior que zero.');
      return;
    }
    if (controlaDRE && !form.planocontas) {
      setError('Informe o Plano de Contas (DFC).');
      return;
    }
    setError('');
    try {
      await createMutation.mutateAsync({
        conta: form.conta,
        data: form.data,
        valor: String(Math.abs(valorNumerico)),
        planocontas: form.planocontas,
        caixa: form.caixa,
        observacao: form.observacao.trim(),
      });
      voltar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao gravar o Saque.');
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Novo Saque"
        subtitle="Retirada da Conta Bancária para o Caixa"
        onBack={voltar}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        <Stack spacing="4" maxW="720px">
          {error && <OktonAlert status="error" description={error} />}

          <Box>
            <Text
              as="label"
              htmlFor="conta"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Conta Bancária *
            </Text>
            <OktonSearchSelect
              name="conta"
              placeholder="Selecione a conta"
              value={form.conta}
              onChange={valor => setForm(f => ({ ...f, conta: valor }))}
              options={[
                ...contas.map(c => ({
                  value: c.id_cts,
                  label: c.descricao_cts,
                })),
              ]}
            />
          </Box>

          <SimpleGrid columns={2} spacing="4">
            <Box>
              <Text
                as="label"
                htmlFor="saque-data"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Data *
              </Text>
              <OktonInput
                name="saque-data"
                type="date"
                size="lg"
                rounded="2xl"
                fontWeight="light"
                color={color}
                value={form.data}
                onChange={e => {
                  const valor = e.target.value;
                  setForm(f => ({ ...f, data: valor }));
                }}
              />
            </Box>
            <Box>
              <Text
                as="label"
                htmlFor="saque-valor"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Valor (R$) *
              </Text>
              <OktonInput
                name="saque-valor"
                type="text"
                inputMode="decimal"
                size="lg"
                rounded="2xl"
                fontWeight="light"
                placeholder="0,00"
                color={color}
                value={form.valor}
                onChange={e => {
                  const valor = e.target.value;
                  setForm(f => ({
                    ...f,
                    valor: limparEntradaValor(valor),
                  }));
                }}
              />
            </Box>
          </SimpleGrid>

          <Box>
            <Text
              as="label"
              htmlFor="planocontas"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Plano de Contas (DFC) {controlaDRE && '*'}
            </Text>
            <OktonSearchSelect
              name="planocontas"
              placeholder="Sem classificação"
              value={form.planocontas}
              onChange={valor => setForm(f => ({ ...f, planocontas: valor }))}
              options={[
                ...planosDFC.map(p => ({
                  value: p.id_pct,
                  label: `${p.item_pct} ${p.nome_pct}`,
                })),
              ]}
            />
          </Box>

          <CaixaField
            valor={form.caixa}
            onChange={caixa => setForm(f => ({ ...f, caixa }))}
          />

          <Box>
            <Text
              as="label"
              htmlFor="saque-observacao"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Observação
            </Text>
            <OktonInput
              name="saque-observacao"
              size="lg"
              rounded="2xl"
              fontWeight="light"
              color={color}
              maxLength={1024}
              value={form.observacao}
              onChange={e => {
                const valor = e.target.value;
                setForm(f => ({ ...f, observacao: valor }));
              }}
            />
          </Box>

          <Text fontSize="xs" color="gray.500">
            Sem Caixa escolhido, o Saque entra no Caixa aberto — como no IFLEX.
            Sem nenhum Caixa aberto o lançamento é recusado.
          </Text>

          <Flex gridGap="3" pt="2">
            <Button variant="ghost" onClick={voltar}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={
                createMutation.isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <Check size={16} />
                )
              }
              isDisabled={createMutation.isLoading}
              onClick={salvar}
            >
              Cadastrar
            </Button>
          </Flex>
        </Stack>
      </Box>
    </BankAppPage>
  );
};

export default NovoSaque;
